/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
// eslint-disable-next-line import/no-cycle
import {configurationApi} from '@api/configuration/api';
import {CONFIGURATION_TYPES} from '@api/constants';
import {documentApi} from '@api/document/api';
import {reminderApi} from '@api/reminder/api';
import {auditApi} from '@api/audit/api';
import {transactionApi} from '@api/transaction/api';
import {recipientApi} from '@api/recipient/api';
import {getRecipientSigningUrl} from '@api/authorizationToken/api';
import {documentVisibilityApi} from '@api/documentVisibility/api';
import {userApi} from '@api/user/api';
import {getApprovalsAndFieldsForDocument, getApprovalsAndFieldsForTransaction} from '@components/Packages/utils/getApprovalsAndFields';
import {getDocumentExtractionError, getErrorCode, getErrorMessage} from '../../utils/requests/error';
import {getIsSenderRecipient} from '../Packages/Recipients/utils';
import {fetchAvatarsByEmails} from '../Packages/Recipients/recipientsSlice';
import {BASE_PATH, TRANSACTION_STATUSES} from '../../../constants';
import {deleteFetchDocumentEque} from '../Designer/designerSlice';
import httpClient from '../../utils/httpClient';

export const transactionSlice = createSlice({
    name: 'transaction',
    initialState: {
        transaction: {},
        approvals: {},
        fields: {},
        schedule: null,
        remindersInfo: {},
        visibility: {},
        audits: {},
        unlockRoleInfo: {},

        isFetchingSchedule: false,
        isUpdatingSchedule: false,
        scheduleError: null,
        isTransactionFetching: false,
        transactionError: null,
        isDocumentsFetching: false,
        documentsFetchError: null,
        isUploadingDocuments: false,
        uploadDocumentsError: {},
        isUpdatingDocuments: false,
        updateDocumentsError: null,
        isUpdatingDocument: false,
        updateDocumentError: null,
        isDeletingDocument: false,
        deleteDocumentError: null,
        isUpdatingSettings: false,
        updateSettingsError: null,
        isFetchingAudits: false,
        isUnlockRoleFetching: false,
        unlockRoleError: null,
        isSavingVisibility: false,
        savingVisibilityError: null,
        isVisibilityFetching: false,
        fetchingVisibilityError: null,
        isUpdatingTransaction: false,
        updateTransactionError: null,
        rolesForLoadingPlaceholders: null,
        isSenderVisibleForLoadingPlaceholders: null,
        transactionSettings: {},
        isFetchingTransactionSettings: false,
        fetchTransactionSettingsError: null,
        signerFederatedIdentities: [],
        isSignerFederatedIdentitiesFetching: false,
        signerFederatedIdentitiesFetchError: null,
        accountIdSignerFederatedIdentitiesFetchedFor: '',
        declineReasons: {},
        isDeclineReasonsFetching: false,
        declineReasonsError: null,
        isFetchingApprovalsAndFields: false
    },
    reducers: {
        setTransaction: (state, action) => {
            state.transaction = action.payload;
        },
        setDocuments: (state, action) => {
            state.transaction.documents = action.payload;
        },
        setRecipients: (state, action) => {
            state.transaction.recipients = action.payload;
        },
        setSchedule: (state, action) => {
            state.schedule = action.payload;
        },
        setRemindersInfo: (state, action) => {
            const {roleId} = action.payload;
            const roleState = get(state, `remindersInfo[${roleId}]`, {});

            state.remindersInfo = {
                ...state.remindersInfo,
                [roleId]: {
                    ...roleState,
                    ...action.payload
                }
            };
        },
        setUnlockRoleInfo: (state, action) => {
            const {roleId} = action.payload;
            const roleState = get(state, `unlockRoleInfo[${roleId}]`, {});

            state.unlockRoleInfo = {
                ...state.unlockRoleInfo,
                [roleId]: {
                    ...roleState,
                    ...action.payload
                }
            };
        },

        setVisibility: (state, action) => {
            state.visibility = action.payload;
        },
        setAudits: (state, action) => {
            state.audits = {
                ...state.audits,
                ...action.payload
            };
        },
        setIsTransactionFetching: (state, action) => {
            state.isTransactionFetching = action.payload;
        },
        setIsDocumentsFetching: (state, action) => {
            state.isDocumentsFetching = action.payload;
        },
        setDocumentsFetchError: (state, action) => {
            state.documentsFetchError = action.payload;
        },
        setTransactionError: (state, action) => {
            state.transactionError = action.payload;
        },
        setTransactionResponseStatusCode: (state, action) => {
            state.transactionResponseStatusCode = action.payload;
        },
        setIsUploadingDocuments: (state, action) => {
            state.isUploadingDocuments = action.payload;
        },
        setUploadDocumentsError: (state, action) => {
            state.uploadDocumentsError = action.payload;
        },
        setIsUpdatingDocuments: (state, action) => {
            state.isUpdatingDocuments = action.payload;
        },
        setUpdateDocumentsError: (state, action) => {
            state.updateDocumentsError = action.payload;
        },
        setIsUpdatingDocument: (state, action) => {
            state.isUpdatingDocument = action.payload;
        },
        setUpdateDocumentError: (state, action) => {
            state.updateDocumentError = action.payload;
        },
        setIsDeletingDocument: (state, action) => {
            state.isDeletingDocument = action.payload;
        },
        setDeleteDocumentError: (state, action) => {
            state.deleteDocumentError = action.payload;
        },
        setIsFetchingSchedule: (state, action) => {
            state.isFetchingSchedule = action.payload;
        },
        setIsUpdatingSchedule: (state, action) => {
            state.isUpdatingSchedule = action.payload;
        },
        setScheduleError: (state, action) => {
            state.scheduleError = action.payload;
        },
        setIsUpdatingSettings: (state, action) => {
            state.isUpdatingSettings = action.payload;
        },
        setUpdateSettingsError: (state, action) => {
            state.updateSettingsError = action.payload;
        },
        setIsUpdatingTransaction: (state, action) => {
            state.isUpdatingTransaction = action.payload;
        },
        setUpdateTransactionError: (state, action) => {
            state.updateTransactionError = action.payload;
        },
        setIsVisibilityFetching: (state, action) => {
            state.isVisibilityFetching = action.payload;
        },
        setFetchingVisibilityError: (state, action) => {
            state.fetchingVisibilityError = action.payload;
        },
        setIsSavingVisibility: (state, action) => {
            state.isSavingVisibility = action.payload;
        },
        setSavingVisibilityError: (state, action) => {
            state.savingVisibilityError = action.payload;
        },
        setIsFetchingAudits: (state, action) => {
            state.isFetchingAudits = action.payload;
        },
        setRolesForLoadingPlaceholders: (state, action) => {
            state.rolesForLoadingPlaceholders = action.payload;
        },
        setIsSenderVisibleForLoadingPlaceholders: (state, action) => {
            state.isSenderVisibleForLoadingPlaceholders = action.payload;
        },
        setTransactionSettings: (state, action) => {
            state.transactionSettings = action.payload;
        },
        setIsFetchingTransactionSettings: (state, action) => {
            state.isFetchingTransactionSettings = action.payload;
        },
        setFetchTransactionSettingsError: (state, action) => {
            state.fetchTransactionSettingsError = action.payload;
        },
        setSignerFederatedIdentities: (state, action) => {
            state.signerFederatedIdentities = action.payload;
        },
        setIsSignerFederatedIdentitiesFetching: (state, action) => {
            state.isSignerFederatedIdentitiesFetching = action.payload;
        },
        setSignerFederatedIdentitiesFetchError: (state, action) => {
            state.signerFederatedIdentitiesFetchError = action.payload;
        },
        setAccountIdSignerFederatedIdentitiesFetchedFor: (state, action) => {
            state.accountIdSignerFederatedIdentitiesFetchedFor = action.payload;
        },
        setDeclineReasonsForTransaction: (state, action) => {
            const {transactionId, declineReasons} = action.payload;
            state.declineReasons[transactionId] = declineReasons;
        },
        setIsDeclineReasonsFetching: (state, action) => {
            state.isDeclineReasonsFetching = action.payload;
        },
        setDeclineReasonsError: (state, action) => {
            state.declineReasonsError = action.payload;
        },
        setIsFetchingApprovalsAndFields: (state, action) => {
            state.isFetchingApprovalsAndFields = action.payload;
        },
        setApprovalsForDocument: (state, action) => {
            const {documentId, approvals} = action.payload;
            state.approvals[documentId] = approvals;
        },
        setFieldsForApproval: (state, action) => {
            const {approvalId, fields} = action.payload;
            state.fields[approvalId] = fields;
        },
        addApproval: (state, action) => {
            const {documentId, approval} = action.payload;
            const list = state.approvals[documentId] || [];
            state.approvals[documentId] = [...list, approval];
        },
        removeApproval: (state, action) => {
            const {documentId, approvalId} = action.payload;
            const list = state.approvals[documentId] || [];
            state.approvals[documentId] = list.filter((a) => a.id !== approvalId);
            delete state.fields[approvalId];
        },
        updateApprovalInState: (state, action) => {
            const {documentId, approval} = action.payload;
            const list = state.approvals[documentId] || [];
            state.approvals[documentId] = list.map((a) => (a.id === approval.id ? approval : a));
        },
        addField: (state, action) => {
            const {approvalId, field} = action.payload;
            const list = state.fields[approvalId] || [];
            state.fields[approvalId] = [...list, field];
        },
        removeField: (state, action) => {
            const {approvalId, fieldId} = action.payload;
            const list = state.fields[approvalId] || [];
            state.fields[approvalId] = list.filter((f) => f.id !== fieldId);
        },
        updateFieldInState: (state, action) => {
            const {approvalId, field} = action.payload;
            const list = state.fields[approvalId] || [];
            state.fields[approvalId] = list.map((f) => (f.id === field.id ? {...f, ...field} : f));
        },
        clearApprovalsForDocument: (state, action) => {
            const {documentId} = action.payload;
            const approvalsList = state.approvals[documentId] || [];
            approvalsList.forEach((a) => { delete state.fields[a.id]; });
            state.approvals[documentId] = [];
        }
    }
});

export const {
    setTransaction,
    setDocuments,
    setRecipients,
    setSchedule,
    setRemindersInfo,
    setIsFetchingSchedule,
    setIsUpdatingSchedule,
    setScheduleError,

    setVisibility,
    setAudits,

    setIsTransactionFetching,
    setTransactionError,
    setTransactionResponseStatusCode,
    setIsDocumentsFetching,
    setDocumentsFetchError,
    setIsUploadingDocuments,
    setUploadDocumentsError,
    setIsUpdatingDocuments,
    setUpdateDocumentsError,
    setIsUpdatingDocument,
    setUpdateDocumentError,
    setIsDeletingDocument,
    setDeleteDocumentError,
    setIsUpdatingSettings,
    setUpdateSettingsError,
    setIsUpdatingTransaction,
    setUpdateTransactionError,
    setIsFetchingAudits,
    setIsVisibilityFetching,
    setFetchingVisibilityError,
    setIsSavingVisibility,
    setSavingVisibilityError,
    setUnlockRoleInfo,
    setIsSenderVisibleForLoadingPlaceholders,
    setRolesForLoadingPlaceholders,
    setTransactionSettings,
    setIsFetchingTransactionSettings,
    setFetchTransactionSettingsError,
    setSignerFederatedIdentities,
    setIsSignerFederatedIdentitiesFetching,
    setSignerFederatedIdentitiesFetchError,
    setAccountIdSignerFederatedIdentitiesFetchedFor,
    setDeclineReasonsForTransaction,
    setIsDeclineReasonsFetching,
    setDeclineReasonsError,
    setIsFetchingApprovalsAndFields,
    setApprovalsForDocument,
    setFieldsForApproval,
    addApproval,
    removeApproval,
    updateApprovalInState,
    addField,
    removeField,
    updateFieldInState,
    clearApprovalsForDocument
} = transactionSlice.actions;

export const resetTransactionErrors = () => async (dispatch) => {
    dispatch(setTransactionError(null));
    dispatch(setTransactionResponseStatusCode(null));
    dispatch(setDocumentsFetchError(null));
    dispatch(setUploadDocumentsError(null));
    dispatch(setUpdateDocumentsError(null));
    dispatch(setUpdateDocumentError(null));
    dispatch(setDeleteDocumentError(null));
    dispatch(setUpdateSettingsError(null));
    dispatch(setScheduleError(null));
};
export const fetchTransactionSettings = ({
    headers, options = {}
} = {}) => async (dispatch, getState) => {
    const {activeAccountId} = getState().user;

    dispatch(setIsFetchingTransactionSettings(true));
    dispatch(setFetchTransactionSettingsError(null));

    try {
        const result = await configurationApi.getConfiguration(
            activeAccountId,
            CONFIGURATION_TYPES.TRANSACTION_SETTINGS,
            {...options, headers}
        );

        dispatch(setTransactionSettings(result.data));
        dispatch(setIsFetchingTransactionSettings(false));
        return true;
    } catch (err) {
        console.log('fetchTransactionSettings error', err);
        dispatch(setIsFetchingTransactionSettings(false));
        dispatch(setFetchTransactionSettingsError(getErrorMessage(err)));
        return false;
    }
};

export const fetchTransaction = ({
    headers, params, options: fetchOptions = {}
} = {}) => async (dispatch, getState) => {
    const {packageId, skipSetEmptyTransaction, skipAvatarsFetch} = params;
    const accountId = get(getState(), 'user.account.id');
    const apiOptions = {};
    if (headers) apiOptions.headers = headers;
    if (fetchOptions.timeout) apiOptions.timeout = fetchOptions.timeout;

    dispatch(setIsTransactionFetching(true));
    if (!skipSetEmptyTransaction) {
        dispatch(setTransaction({}));
    }
    dispatch(setTransactionError(null));
    dispatch(setTransactionResponseStatusCode(null));

    try {
        const [transactionResult, documentsResult, recipientsResult] = await Promise.all([
            transactionApi.getTransaction({accountId, transactionId: packageId, ...apiOptions}),
            documentApi.getAllDocuments(packageId, undefined, apiOptions),
            recipientApi.getAllRecipients(packageId, undefined, apiOptions)
        ]);

        const [visibilitySettled, scheduleSettled] = await Promise.allSettled([
            documentVisibilityApi.getTransactionVisibility(packageId, apiOptions),
            reminderApi.getSchedules(packageId, apiOptions)
        ]);

        const visibility = visibilitySettled.status === 'fulfilled'
            ? visibilitySettled.value.configurations
            : [];
        const scheduleRaw = scheduleSettled.status === 'fulfilled'
            ? scheduleSettled.value.data
            : null;
        const schedule = Array.isArray(scheduleRaw) ? scheduleRaw[0] || null : scheduleRaw || null;

        const documents = documentsResult;
        const {approvalsByDocumentId, fieldsByApprovalId} = await getApprovalsAndFieldsForTransaction(
            packageId,
            documents.map((doc) => doc.id),
            apiOptions
        );

        Object.entries(approvalsByDocumentId).forEach(([documentId, approvals]) => {
            dispatch(setApprovalsForDocument({documentId, approvals}));
        });
        Object.entries(fieldsByApprovalId).forEach(([approvalId, fields]) => {
            dispatch(setFieldsForApproval({approvalId, fields}));
        });

        const recipients = recipientsResult;
        const ownerId = transactionResult.data.owner_id;
        const ownerRecipient = recipients.find((r) => getIsSenderRecipient(r, ownerId));
        let sender = null;
        if (ownerRecipient) {
            sender = {
                id: ownerRecipient.id,
                email: ownerRecipient.email,
                firstName: ownerRecipient.first_name,
                lastName: ownerRecipient.last_name,
                company: ownerRecipient.organisation,
                title: ownerRecipient.title
            };
        } else if (ownerId) {
            const currentUser = get(getState(), 'user.user') || {};
            if (currentUser.id === ownerId) {
                sender = {
                    id: currentUser.id,
                    email: currentUser.email,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    company: currentUser.company || currentUser.organisation,
                    title: currentUser.title
                };
            } else {
                try {
                    const ownerResult = await userApi.getUser({userId: ownerId, options: apiOptions});
                    const ownerData = ownerResult.data || {};
                    sender = {
                        id: ownerData.id,
                        email: ownerData.email,
                        firstName: ownerData.first_name,
                        lastName: ownerData.last_name,
                        company: ownerData.organisation,
                        title: ownerData.title
                    };
                } catch (ownerErr) {
                    console.error('fetchTransaction: failed to fetch owner user', ownerErr);
                }
            }
        }
        const transaction = {
            ...transactionResult.data,
            documents,
            recipients,
            visibility,
            schedule,
            sender
        };

        if (!skipAvatarsFetch) {
            const recipientEmails = recipients.map((r) => r.email).filter(Boolean);
            await dispatch(fetchAvatarsByEmails({headers, emails: recipientEmails, options: fetchOptions}));
        }

        dispatch(setTransaction(transaction));
        dispatch(setSchedule(schedule));
        dispatch(setTransactionResponseStatusCode(200));
        return transaction;
    } catch (err) {
        dispatch(setTransactionError(getErrorMessage(err)));
        dispatch(setTransactionResponseStatusCode(getErrorCode(err)));
        return false;
    } finally {
        dispatch(setIsTransactionFetching(false));
    }
};

/** @param {{transactionId?: string, options?: object}} [params] */
export const fetchDeclineReasons = ({transactionId, options = {}} = {}) => async (dispatch, getState) => {
    const accountId = get(getState(), 'user.account.id');
    dispatch(setIsDeclineReasonsFetching(true));
    dispatch(setDeclineReasonsError(null));
    try {
        const result = await transactionApi.getDeclineReasons({
            accountId,
            transactionId,
            ...options
        });
        const declineReasons = result?.data || [];
        dispatch(setDeclineReasonsForTransaction({transactionId, declineReasons}));
        return declineReasons;
    } catch (err) {
        dispatch(setDeclineReasonsError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsDeclineReasonsFetching(false));
    }
};

export const updateTransaction = (payload) => async (dispatch, getState) => {
    const state = getState();
    const transaction = get(state, 'transaction.transaction');
    const accountId = get(state, 'user.account.id');
    const userId = get(state, 'user.user.id');
    try {
        const result = await transactionApi.updateTransaction({
            accountId,
            userId,
            transactionId: transaction.id,
            data: getUpdatableTransactionFields(payload)
        });
        dispatch(setTransaction({
            ...transaction,
            ...result.data
        }));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    }
};

export const refreshDocuments = () => async (dispatch, getState) => {
    const transactionId = get(getState(), 'transaction.transaction.id');

    if (!transactionId) {
        return false;
    }
    dispatch(setIsDocumentsFetching(true));
    dispatch(setDocumentsFetchError(null));

    try {
        const documents = await documentApi.getAllDocuments(transactionId);
        const existingDocs = get(getState(), 'transaction.transaction.documents', []);
        const mergedDocs = documents.map((doc) => {
            const existing = existingDocs.find((d) => d.id === doc.id);
            return existing ? {...doc, approvals: existing.approvals || []} : doc;
        });
        dispatch(setDocuments(mergedDocs));
        dispatch(setIsDocumentsFetching(false));
        dispatch(setDocumentsFetchError(null));
        await dispatch(deleteFetchDocumentEque());
        return mergedDocs;
    } catch (err) {
        dispatch(setIsDocumentsFetching(false));
        dispatch(setDocumentsFetchError(getErrorMessage(err)));
        return false;
    }
};

export const refreshRecipients = () => async (dispatch, getState) => {
    const transactionId = get(getState(), 'transaction.transaction.id');

    if (!transactionId) {
        return false;
    }

    try {
        const result = await recipientApi.getAllRecipients(transactionId);
        dispatch(setRecipients(result));
        return result;
    } catch (err) {
        return false;
    }
};

/**
 * Refetches the transaction itself together with its recipients.
 * Needed after a document upload: the backend may add a recipient from a text-tag
 * or flag the owner as a recipient through `settings.owner_is_recipient`.
 */
export const refreshTransactionAndRecipients = () => async (dispatch, getState) => {
    const transactionId = get(getState(), 'transaction.transaction.id');
    const accountId = get(getState(), 'user.account.id');

    if (!transactionId) {
        return false;
    }

    try {
        const [transactionResult, recipients] = await Promise.all([
            transactionApi.getTransaction({accountId, transactionId}),
            recipientApi.getAllRecipients(transactionId)
        ]);
        const currentTransaction = get(getState(), 'transaction.transaction', {});

        dispatch(setTransaction({
            ...currentTransaction,
            ...transactionResult.data,
            recipients
        }));
        return true;
    } catch (err) {
        return false;
    }
};

export const handleFetchTransaction = () => async (dispatch, getState) => {
    const packageId = get(getState(), 'transaction.transaction.id');

    if (!packageId) {
        return false;
    }

    try {
        const transaction = await dispatch(fetchTransaction({
            params: {packageId, skipSetEmptyTransaction: true, skipAvatarsFetch: false}
        }));

        if (!transaction) {
            return false;
        }

        return {recipients: transaction.recipients, documents: transaction.documents};
    } catch (err) {
        dispatch(setTransactionError(getErrorMessage(err)));
        return false;
    }
};

export const uploadDocuments = ({
    packageId, documents
}) => async (dispatch, getState) => {
    dispatch(setIsUploadingDocuments(true));
    dispatch(setUploadDocumentsError(null));

    try {
        const existingDocuments = get(getState(), 'transaction.transaction.documents', []);
        const startOrder = existingDocuments.length;
        const uploadResults = [];
        for (let i = 0; i < documents.length; i += 1) {
            const doc = documents[i];
            const formData = new FormData();
            const payload = [{
                name: doc.name || doc.file?.name,
                order: doc.order ?? (startOrder + i),
                ...(doc.deprecated_id && {deprecated_id: doc.deprecated_id}),
                ...(doc.data && {data: doc.data})
            }];
            formData.append('payloads', new Blob([JSON.stringify(payload)], {type: 'application/json'}));
            formData.append('file', doc.file || doc);
            const result = await documentApi.createDocuments(packageId, formData);
            uploadResults.push(...(Array.isArray(result.data) ? result.data : [result.data]));
        }
        const uploadedDocuments = uploadResults;
        const currentDocuments = get(getState(), 'transaction.transaction.documents', []);
        dispatch(setDocuments([...currentDocuments, ...uploadedDocuments]));
        dispatch(fetchApprovalsAndFields(packageId));
        await dispatch(refreshTransactionAndRecipients());
        dispatch(setIsUploadingDocuments(false));
        return !isEmpty(uploadedDocuments) ? uploadedDocuments : null;
    } catch (err) {
        dispatch(setIsUploadingDocuments(false));
        dispatch(setUploadDocumentsError(getDocumentExtractionError(err)));
        return null;
    }
};

export const updateDocuments = ({
    packageId, documents
}) => async (dispatch) => {
    dispatch(setIsUpdatingDocuments(true));
    dispatch(setUpdateDocumentsError(null));

    try {
        await Promise.all(
            documents.map((doc) => documentApi.updateDocument(packageId, doc.id, {order: doc.order}))
        );
        await dispatch(refreshDocuments());
        dispatch(setIsUpdatingDocuments(false));
        return true;
    } catch (err) {
        dispatch(setUpdateDocumentsError('esl.feedback.document_reorder.error'));
        dispatch(setIsUpdatingDocuments(false));
        return false;
    }
};

export const updateDocument = ({
    packageId, document
}) => async (dispatch) => {
    dispatch(setIsUpdatingDocument(true));
    dispatch(setUpdateDocumentError(null));

    try {
        await documentApi.updateDocument(packageId, document.id, document);
        await dispatch(refreshDocuments());
        dispatch(setIsUpdatingDocument(false));
        return true;
    } catch (err) {
        dispatch(setIsUpdatingDocument(false));
        dispatch(setUpdateDocumentError('esl.feedback.document_reorder.error'));
        return false;
    }
};

export const deleteDocument = ({
    packageId, documentId, callback
}) => async (dispatch, getState) => {
    const transaction = get(getState(), 'transaction.transaction');

    dispatch(setIsDeletingDocument(true));
    dispatch(setDeleteDocumentError(null));

    try {
        await documentApi.deleteDocument(packageId, documentId);
        dispatch(setTransaction({
            ...transaction,
            documents: transaction.documents.filter(({id}) => id !== documentId)
        }));
        dispatch(fetchApprovalsAndFields(packageId));
        dispatch(setIsDeletingDocument(false));
        if (callback) {
            callback();
        }
        return true;
    } catch (err) {
        dispatch(setIsDeletingDocument(false));
        dispatch(setDeleteDocumentError('esl.feedback.document_reorder.error'));
        return false;
    }
};

function getUpdatableTransactionFields(payload) {
    const fields = {};
    const allowedKeys = [
        'name', 'description', 'language', 'timezone', 'origin',
        'message_to_recipients', 'autocomplete',
        'retain_until', 'expires_at', 'attributes'
    ];
    allowedKeys.forEach((key) => {
        if (payload[key] !== undefined) {
            fields[key] = payload[key];
        }
    });
    if (payload.settings) {
        const allowedSettingsKeys = [
            'owner_is_recipient',
            'face_to_face',
            'font_size'
        ];
        const filteredSettings = {};
        allowedSettingsKeys.forEach((key) => {
            if (payload.settings[key] !== undefined) {
                filteredSettings[key] = payload.settings[key];
            }
        });
        if (Object.keys(filteredSettings).length > 0) {
            fields.settings = filteredSettings;
        }
    }
    return fields;
}

export const updateSettings = (payload) => async (dispatch, getState) => {
    const state = getState();
    const transaction = get(state, 'transaction.transaction');
    const accountId = get(state, 'user.account.id');
    const userId = get(state, 'user.user.id');

    dispatch(setIsUpdatingSettings(true));
    dispatch(setUpdateSettingsError(null));

    try {
        const result = await transactionApi.updateTransaction({
            accountId,
            userId,
            transactionId: transaction.id,
            data: getUpdatableTransactionFields(payload)
        });
        dispatch(setTransaction({
            ...transaction,
            ...result.data
        }));
        dispatch(setIsUpdatingSettings(false));

        return true;
    } catch (err) {
        dispatch(setUpdateSettingsError(getErrorMessage(err)));
        dispatch(setIsUpdatingSettings(false));

        return false;
    }
};

export const fetchSchedule = ({
    headers, baseUrl, params, options: fetchOptions = {}
} = {}) => async (dispatch) => {
    const {packageId} = params;
    const requestOptions = {};
    if (headers) requestOptions.headers = headers;
    if (fetchOptions.timeout) requestOptions.timeout = fetchOptions.timeout;
    if (baseUrl) requestOptions.baseUrl = baseUrl;

    dispatch(setIsFetchingSchedule(true));
    try {
        const result = await reminderApi.getSchedules(packageId, requestOptions);
        const rawData = result.data;
        const schedule = Array.isArray(rawData) ? rawData[0] || null : rawData || null;
        dispatch(setSchedule(schedule));
        return schedule;
    } catch (err) {
        dispatch(setScheduleError(getErrorMessage(err)));
        return null;
    } finally {
        dispatch(setIsFetchingSchedule(false));
    }
};

export const upsertSchedule = (packageId, scheduleData) => async (dispatch, getState) => {
    const transactionId = packageId || get(getState(), 'transaction.transaction.id');
    dispatch(setIsUpdatingSchedule(true));
    dispatch(setScheduleError(null));
    try {
        const result = await reminderApi.createOrUpdateSchedule(transactionId, scheduleData);
        dispatch(setSchedule(result.data));
        return true;
    } catch (err) {
        dispatch(setScheduleError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingSchedule(false));
    }
};

export const removeSchedule = () => async (dispatch, getState) => {
    const transactionId = get(getState(), 'transaction.transaction.id');
    dispatch(setIsUpdatingSchedule(true));
    dispatch(setScheduleError(null));
    try {
        await reminderApi.deleteSchedule(transactionId);
        dispatch(setSchedule(null));
        return true;
    } catch (err) {
        dispatch(setScheduleError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingSchedule(false));
    }
};

export const sendRecipientReminder = (packageId, roleId) => async (dispatch) => {
    dispatch(setRemindersInfo({
        roleId,
        wasSuccessful: false,
        isSending: true,
        error: null
    }));

    try {
        await reminderApi.sendNotification(packageId, roleId);
        dispatch(setRemindersInfo({
            roleId,
            wasSuccessful: true,
            isSending: false,
            error: null
        }));
        return true;
    } catch (err) {
        dispatch(setRemindersInfo({
            roleId,
            wasSuccessful: false,
            isSending: false,
            error: getErrorMessage(err)
        }));
        return false;
    }
};

export const unlockRole = (packageId, roleId) => async (dispatch) => {
    dispatch(setUnlockRoleInfo({
        roleId,
        wasSuccessful: false,
        isFetching: true,
        error: null
    }));
    try {
        await recipientApi.unlockRecipient(packageId, roleId);
        dispatch(setUnlockRoleInfo({
            roleId,
            wasSuccessful: true,
            isFetching: false,
            error: null
        }));
        return true;
    } catch (error) {
        dispatch(setUnlockRoleInfo({
            roleId,
            wasSuccessful: false,
            isFetching: false,
            error
        }));
        return false;
    }
};

export const fetchAudits = (packageId) => async (dispatch) => {
    dispatch(setIsFetchingAudits(true));
    try {
        const result = await auditApi.getAudits(packageId, {per_page: 100});
        const audits = Array.isArray(result.data) ? result.data : get(result, 'data.data', []);
        dispatch(setAudits({
            [packageId]: audits
        }));
        return true;
    } catch (err) {
        return false;
    } finally {
        dispatch(setIsFetchingAudits(false));
    }
};

export const fetchVisibility = (packageId, options = {}) => async (dispatch, getState) => {
    const {transaction} = getState();
    const requestOptions = {};
    if (options.headers) requestOptions.headers = options.headers;
    if (options.timeout) requestOptions.timeout = options.timeout;

    dispatch(setIsVisibilityFetching(true));
    dispatch(setFetchingVisibilityError(null));

    try {
        const result = await documentVisibilityApi.getTransactionVisibility(packageId, requestOptions);
        dispatch(setVisibility({
            ...transaction.visibility,
            [packageId]: result.data || []
        }));
        return true;
    } catch (err) {
        dispatch(setFetchingVisibilityError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsVisibilityFetching(false));
    }
};

export const saveVisibility = ({packageId, configurations}) => async (dispatch, getState) => {
    dispatch(setIsSavingVisibility(true));
    dispatch(setSavingVisibilityError(null));

    try {
        const results = await Promise.allSettled(
            configurations.map((config) => documentVisibilityApi.updateDocumentVisibility(
                packageId,
                config.document_id,
                {visibilities: config.visibilities}
            ))
        );
        const hasFailures = results.some((r) => r.status === 'rejected');
        if (hasFailures) {
            dispatch(setSavingVisibilityError('esl.feedback.layout_save.error'));
            return false;
        }
        const updatedConfigurations = configurations.map((config, idx) => ({
            document_id: config.document_id,
            visibilities: results[idx].value.data || config.visibilities
        }));
        const {transaction} = getState();
        dispatch(setVisibility({
            ...transaction.visibility,
            [packageId]: updatedConfigurations
        }));
        return true;
    } catch (err) {
        dispatch(setSavingVisibilityError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsSavingVisibility(false));
    }
};

export const fetchDocument = (packageId, documentId) => async (dispatch, getState) => {
    const transaction = get(getState(), 'transaction.transaction');

    try {
        const [documentResult, {approvalsByDocumentId, fieldsByApprovalId}] = await Promise.all([
            documentApi.getDocument(packageId, documentId),
            getApprovalsAndFieldsForDocument(packageId, documentId)
        ]);
        dispatch(setApprovalsForDocument({documentId, approvals: approvalsByDocumentId[documentId]}));
        Object.entries(fieldsByApprovalId).forEach(([approvalId, fields]) => {
            dispatch(setFieldsForApproval({approvalId, fields}));
        });

        dispatch(setTransaction({
            ...transaction,
            documents: transaction.documents.map((document) => (
                document.id === documentId ? documentResult.data : document
            ))
        }));
        return true;
    } catch (err) {
        return false;
    }
};

export const fetchApprovalsAndFields = (packageId) => async (dispatch, getState) => {
    const documents = get(getState(), 'transaction.transaction.documents', []);

    if (!packageId || !documents.length) {
        return;
    }

    dispatch(setIsFetchingApprovalsAndFields(true));

    try {
        const {approvalsByDocumentId, fieldsByApprovalId} = await getApprovalsAndFieldsForTransaction(
            packageId,
            documents.map((doc) => doc.id)
        );

        Object.entries(approvalsByDocumentId).forEach(([documentId, approvals]) => {
            dispatch(setApprovalsForDocument({documentId, approvals}));
        });
        Object.entries(fieldsByApprovalId).forEach(([approvalId, fields]) => {
            dispatch(setFieldsForApproval({approvalId, fields}));
        });
    } finally {
        dispatch(setIsFetchingApprovalsAndFields(false));
    }
};

function getAccountUserIds(state) {
    return {
        accountId: get(state, 'user.account.id'),
        userId: get(state, 'user.user.id')
    };
}

export const moveToDraft = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const currentTransaction = get(state, 'transaction.transaction', {});
    const id = transactionId || currentTransaction.id;
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));

    try {
        const result = await transactionApi.setTransactionToDraft({
            accountId, userId, transactionId: id
        });
        dispatch(setTransaction({...currentTransaction, ...result.data}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const moveToTrash = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const currentTransaction = get(state, 'transaction.transaction', {});
    const id = transactionId || currentTransaction.id;
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));

    try {
        const result = await transactionApi.trashTransaction({
            accountId, userId, transactionId: id
        });
        dispatch(setTransaction({...currentTransaction, ...result.data}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const complete = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const currentTransaction = get(state, 'transaction.transaction', {});
    const id = transactionId || currentTransaction.id;
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));

    try {
        const result = await transactionApi.updateTransaction({
            accountId,
            userId,
            transactionId: id,
            data: {status: TRANSACTION_STATUSES.COMPLETED}
        });
        dispatch(setTransaction({...currentTransaction, ...result.data}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const deletePermanently = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const id = transactionId || get(state, 'transaction.transaction.id');
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));
    try {
        await transactionApi.deleteTransaction({
            accountId, userId, transactionId: id
        });
        dispatch(setTransaction({}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const restore = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const currentTransaction = get(state, 'transaction.transaction', {});
    const id = transactionId || currentTransaction.id;
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));
    try {
        const result = await transactionApi.restoreTransaction({
            accountId, userId, transactionId: id
        });
        dispatch(setTransaction({...currentTransaction, ...result.data}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const archive = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const currentTransaction = get(state, 'transaction.transaction', {});
    const id = transactionId || currentTransaction.id;
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));
    try {
        const result = await transactionApi.archiveTransaction({
            accountId, userId, transactionId: id
        });
        dispatch(setTransaction({...currentTransaction, ...result.data}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const unArchive = (transactionId) => async (dispatch, getState) => {
    const state = getState();
    const currentTransaction = get(state, 'transaction.transaction', {});
    const id = transactionId || currentTransaction.id;
    const {accountId, userId} = getAccountUserIds(state);

    dispatch(setIsUpdatingTransaction(true));
    dispatch(setUpdateTransactionError(null));
    try {
        const result = await transactionApi.unarchiveTransaction({
            accountId, userId, transactionId: id
        });
        dispatch(setTransaction({...currentTransaction, ...result.data}));
        return true;
    } catch (err) {
        dispatch(setUpdateTransactionError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsUpdatingTransaction(false));
    }
};

export const fetchSigningUrl = (transactionId, recipientId) => async () => {
    try {
        const result = await getRecipientSigningUrl(transactionId, recipientId);
        return get(result, 'data', {});
    } catch (err) {
        return false;
    }
};

export const fetchSignerFederatedIdentities = (accountId) => async (dispatch, getState) => {
    const {config} = getState().app;
    dispatch(setIsSignerFederatedIdentitiesFetching(true));
    try {
        const encodedAccountId = encodeURIComponent(accountId);
        const url = `${BASE_PATH}/s2s/sso/signer-federated-identities?accountId=${encodedAccountId}`;
        const result = await httpClient.get(url, {
            withCredentials: false,
            timeout: config.VS_ENV === 'development' ? 5000 : undefined
        });
        const identities = get(result, 'data', []);
        dispatch(setSignerFederatedIdentities(identities));
        dispatch(setAccountIdSignerFederatedIdentitiesFetchedFor(accountId));
        return result.data;
    } catch (err) {
        dispatch(setSignerFederatedIdentities([]));
        dispatch(setSignerFederatedIdentitiesFetchError(getErrorMessage(err)));
        dispatch(setAccountIdSignerFederatedIdentitiesFetchedFor(accountId));
        return false;
    } finally {
        dispatch(setIsSignerFederatedIdentitiesFetching(false));
    }
};

export const selectTransaction = (state) => state.transaction.transaction;
export const selectRecipients = (state) => state.transaction.transaction.recipients || [];
export const selectSchedule = (state) => state.transaction.schedule;
export const selectRemindersInfo = (state) => state.transaction.remindersInfo;
export const selectIsFetchingSchedule = (state) => state.transaction.isFetchingSchedule;
export const selectIsUpdatingSchedule = (state) => state.transaction.isUpdatingSchedule;
export const selectScheduleError = (state) => state.transaction.scheduleError;
export const selectVisibility = (state) => state.transaction.visibility;
export const selectIsVisibilityFetching = (state) => state.transaction.isVisibilityFetching;
export const selectIsSavingVisibility = (state) => state.transaction.isSavingVisibility;
export const selectAudits = (state) => state.transaction.audits;
export const selectUnlockRoleInfo = (state) => state.transaction.unlockRoleInfo;
export const selectIsTransactionFetching = (state) => state.transaction.isTransactionFetching;
export const selectIsDocumentsFetching = (state) => state.transaction.isDocumentsFetching;
export const selectIsUploadingDocuments = (state) => state.transaction.isUploadingDocuments;
export const selectIsUpdatingDocuments = (state) => state.transaction.isUpdatingDocuments;
export const selectIsDeletingDocument = (state) => state.transaction.isDeletingDocument;
export const selectIsUpdatingDocument = (state) => state.transaction.isUpdatingDocument;
export const selectIsUpdatingSettings = (state) => state.transaction.isUpdatingSettings;
export const selectTransactionError = (state) => state.transaction.transactionError;
export const selectTransactionResponceStatusCode = (state) => state.transaction.transactionResponseStatusCode;
export const selectDocumentsFetchError = (state) => state.transaction.documentsFetchError;
export const selectUploadDocumentsError = (state) => state.transaction.uploadDocumentsError;
export const selectUpdateDocumentsError = (state) => state.transaction.updateDocumentsError;
export const selectDeleteDocumentError = (state) => state.transaction.deleteDocumentError;
export const selectUpdateDocumentError = (state) => state.transaction.updateDocumentError;
export const selectUpdateSettingsError = (state) => state.transaction.updateSettingsError;
export const selectUpdateTransactionError = (state) => state.transaction.updateTransactionError;
export const selectRolesForLoadingPlaceholders = (state) => state.transaction.rolesForLoadingPlaceholders;
export const selectIsSenderVisibleForLoadingPlaceholders = (state) => state.transaction.isSenderVisibleForLoadingPlaceholders;
export const selectTransactionSettings = (state) => state.transaction.transactionSettings;
export const selectIsFetchingTransactionSettings = (state) => state.transaction.isFetchingTransactionSettings;
export const selectFetchTransactionSettingsError = (state) => state.transaction.fetchTransactionSettingsError;
export const selectSignerFederatedIdentities = (state) => state.transaction.signerFederatedIdentities;
export const selectIsSignerFederatedIdentitiesFetching = (state) => state.transaction.isSignerFederatedIdentitiesFetching;
export const selectSignerFederatedIdentitiesFetchError = (state) => state.transaction.signerFederatedIdentitiesFetchError;
export const selectAccountIdSignerFederatedIdentitiesFetchedFor = (state) => state.transaction.accountIdSignerFederatedIdentitiesFetchedFor;
export const selectIsFetchingApprovalsAndFields = (state) => state.transaction.isFetchingApprovalsAndFields;

export const selectApprovals = (state) => state.transaction.approvals;
export const selectFields = (state) => state.transaction.fields;
export const selectApprovalsByDocumentId = (documentId) => (state) => state.transaction.approvals[documentId] || [];
export const selectFieldsByApprovalId = (approvalId) => (state) => state.transaction.fields[approvalId] || [];
const EMPTY_DECLINE_REASONS = [];
export const selectDeclineReasonsByTransactionId = (transactionId) => (state) => state.transaction.declineReasons[transactionId] || EMPTY_DECLINE_REASONS;
export const selectIsDeclineReasonsFetching = (state) => state.transaction.isDeclineReasonsFetching;
export const selectDeclineReasonsError = (state) => state.transaction.declineReasonsError;

export default transactionSlice.reducer;
